import { publicCreatePaymentIntent } from "@babackapp/private-api-client/client/use-public-create-payment-intent";
import { useMutation } from "@tanstack/react-query";
import type { Order, ReturnDraft } from "../types";

type CreatePaymentIntentInput = {
  draft: ReturnDraft;
  order: Order;
  checkoutToken: string;
};

type IntentLineItem = {
  lineItemId: string;
  quantity: number;
  resolution: string;
  variantId: string;
};

/**
 * Build the `itemsToReturn` payload for the checkout intent.
 *
 * The backend nets the returned items' value against the rebuy/exchange articles:
 * - `exchange` items provide the NEW variant to ship (`exchangeVariantId`).
 * - `exchange-credit` items provide the credit deducted from the amount due. The
 *   backend looks them up by the ORIGINAL variant id, so it must be resolved from
 *   the order line item — the draft only stores `lineItemId`. Omitting these items
 *   makes the backend compute a zero credit and charge the full rebuy price.
 */
function buildItemsToReturn(draft: ReturnDraft, order: Order): IntentLineItem[] {
  const originalVariantByLineItemId = new Map(
    (order.lineItems ?? []).map((li) => [li.line_item_id, li.variant.variant_id]),
  );

  return draft.lineItems.flatMap((li) => {
    if (li.resolution === "exchange" && li.exchangeVariantId) {
      return [
        {
          lineItemId: li.lineItemId,
          quantity: li.quantity,
          resolution: li.resolution,
          variantId: li.exchangeVariantId,
        },
      ];
    }
    if (li.resolution === "exchange-credit") {
      const variantId = originalVariantByLineItemId.get(li.lineItemId);
      if (!variantId) return [];
      return [
        {
          lineItemId: li.lineItemId,
          quantity: li.quantity,
          resolution: li.resolution,
          variantId,
        },
      ];
    }
    return [];
  });
}

export function useCreatePaymentIntent() {
  return useMutation({
    mutationFn: ({ draft, order, checkoutToken }: CreatePaymentIntentInput) =>
      publicCreatePaymentIntent({
        shopId: draft.shopId,
        orderId: draft.orderId,
        checkoutToken,
        rateHandle: draft.shippingRateId ?? undefined,
        itemsToReturn: buildItemsToReturn(draft, order),
        rebuyItems: draft.rebuyItems?.length
          ? draft.rebuyItems.map((item) => ({
              variantId: String(item.variantId),
              quantity: item.quantity,
              price: item.price,
            }))
          : null,
      }),
  });
}
